import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs, { Dayjs } from 'dayjs'
import CheckBox from '../CheckBox/CheckBox'
import TimePicker from '../TimePicker/TimePicker'
import styles from './OpeningHoursEditor.module.scss'

/** One row of the week. The same shape the website document stores. */
export interface OpeningHoursEntry {
  /** 0 = Monday, matching how the roster in this repo already counts days. */
  weekday: number
  closed: boolean
  opensAt: string
  closesAt: string
}

interface OpeningHoursEditorProps {
  hours: OpeningHoursEntry[]
  onChange: (hours: OpeningHoursEntry[]) => void
  /** Which language the weekday names are spelled in. */
  language: string
  disabled?: boolean
}

/*
  Every minute selectable. A venue that closes at 22:30 is ordinary, and a step of
  five would make it unreachable.
*/
const HOUR_MINUTES_STEP = 1

const TIME_FORMAT = 'HH:mm'

const toTime = (value: string) => {
  const parsed = dayjs(value, TIME_FORMAT)
  return parsed.isValid() ? parsed : null
}

/**
 * The seven days a venue is open, as one grid.
 *
 * Shared because two screens ask the same question — the restaurant settings
 * panel, which owns the answer, and the website builder, whose page renders it.
 * The layout comments above and in the stylesheet record measurements rather than
 * preferences, which is the reason this is one component and not two that look
 * alike.
 */
function OpeningHoursEditor(props: OpeningHoursEditorProps) {
  const { hours, onChange, language, disabled } = props
  const { t } = useTranslation()

  // 2024-01-01 was a Monday, which is weekday 0 in this app's counting.
  const weekdayName = useMemo(() => {
    const format = new Intl.DateTimeFormat(language, { weekday: 'long' })
    return (weekday: number) =>
      format.format(new Date(Date.UTC(2024, 0, 1 + weekday)))
  }, [language])

  const setEntry = (weekday: number, values: Partial<OpeningHoursEntry>) =>
    onChange(
      hours.map(entry =>
        entry.weekday === weekday ? { ...entry, ...values } : entry
      )
    )

  /*
    An emptied or half typed picker is ignored rather than written through. The
    schema wants "HH:mm" and nothing else, and a day that is open has no way to
    say "no opening time": that is what the Closed box is for. Writing the blank
    would leave a document that cannot be published, over a keystroke.
  */
  const setTime = (
    weekday: number,
    field: 'opensAt' | 'closesAt',
    value: Dayjs | null
  ) => {
    if (!value?.isValid()) return
    setEntry(weekday, { [field]: value.format(TIME_FORMAT) })
  }

  return (
    <div className={styles.week}>
      {hours.map(entry => (
        <div key={entry.weekday} className={styles.hourRow}>
          <span className={`body-medium ${styles.dayName}`}>
            {weekdayName(entry.weekday)}
          </span>
          {!entry.closed && (
            <div className={styles.hourFields}>
              {/*
                Each picker in a box of its own. The shared control sets
                `width: 100%` through `sx` and an emotion class beats a CSS
                module class, so it cannot be sized from here directly; given a
                parent to fill, it fills that instead.
              */}
              <div className={styles.hourField}>
                <TimePicker
                  label={t('COMMON.FROM')}
                  pinnedLabel
                  minutesStep={HOUR_MINUTES_STEP}
                  value={toTime(entry.opensAt)}
                  disabled={disabled}
                  onChange={value => setTime(entry.weekday, 'opensAt', value)}
                />
              </div>
              <div className={styles.hourField}>
                <TimePicker
                  label={t('COMMON.TO')}
                  pinnedLabel
                  minutesStep={HOUR_MINUTES_STEP}
                  value={toTime(entry.closesAt)}
                  disabled={disabled}
                  onChange={value => setTime(entry.weekday, 'closesAt', value)}
                />
              </div>
            </div>
          )}
          {/*
            Last in the row and hard against the right edge. Sitting between the
            day and its hours it read as a second name for the day, and a day
            that was open showed a box with nothing to do with the fields
            wrapped underneath it.
          */}
          <div className={styles.dayClosed}>
            <CheckBox
              label={t('COMMON.CLOSED')}
              checked={entry.closed}
              value={entry.closed}
              disabled={disabled}
              onChange={closed => setEntry(entry.weekday, { closed })}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

export default OpeningHoursEditor
